import type { components } from "@/api/openapi";
import { typedApi } from "@/api/typed-client";
import { handleEtagMismatch } from "@/components/recipes/etag-toast-bridge";
import { getErrorMessage } from "@/lib/errors";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

// Re-export schema types
export type RecipeCreate = components["schemas"]["RecipeCreate"];
export type RecipeUpdate = components["schemas"]["RecipeUpdate"];
export type RecipeCommit = components["schemas"]["RecipeCommit"];
export type RecipeRelationCreate = components["schemas"]["RecipeRelationCreate"];
export type RecipeUploadRequest = components["schemas"]["RecipeUploadRequest"];
export type RecipeUploadConfirm = components["schemas"]["RecipeUploadConfirm"];
export type RecipeMutationResponse = components["schemas"]["RecipeMutationResponse"];
export type RecipeCommitResponse = components["schemas"]["RecipeCommitResponse"];
export type RecipeUploadRequestResponse = components["schemas"]["RecipeUploadRequestResponse"];
export type RecipeUploadConfirmResponse = components["schemas"]["RecipeUploadConfirmResponse"];

/**
 * Create a new recipe.
 */
export function useCreateRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: RecipeCreate) => {
      const { data, error } = await typedApi.POST("/v1/recipes", {
        body: input,
      });

      if (error) {
        throw new Error(getErrorMessage(error) || "Failed to create recipe");
      }

      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      toast.success("Recipe created");
    },
    onError: (e) => {
      toast.error(`Create failed: ${(e as Error).message}`);
    },
  });
}

/**
 * Update an existing recipe.
 * Checks for 409 etag conflicts and shows a reload toast.
 */
export function useUpdateRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      input,
    }: {
      recipeId: string;
      input: RecipeUpdate;
    }) => {
      const result = await typedApi.PATCH("/v1/recipes/{recipe_id}", {
        params: { path: { recipe_id: recipeId } },
        body: input,
      });

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to update recipe");
      }

      return { recipeId, etag: (result.data as { etag: string }).etag };
    },
    onSuccess: ({ recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      toast.success("Recipe updated");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Update failed: ${msg}`);
      }
    },
  });
}

/**
 * Commit a new version of a recipe.
 */
export function useCommitRecipe() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      input,
    }: {
      recipeId: string;
      input: RecipeCommit;
    }) => {
      const result = await typedApi.POST("/v1/recipes/{recipe_id}/commit", {
        params: { path: { recipe_id: recipeId } },
        body: input,
      });

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to commit recipe");
      }

      return { data: result.data, recipeId };
    },
    onSuccess: ({ data, recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipes"] });
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      qc.invalidateQueries({ queryKey: ["recipe", recipeId, "versions"] });
      toast.success(`Version ${data.version_no} committed`);
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Commit failed: ${msg}`);
      }
    },
  });
}

/**
 * Add a relation to a recipe.
 */
export function useAddRelation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      input,
    }: {
      recipeId: string;
      input: RecipeRelationCreate;
    }) => {
      const result = await typedApi.POST("/v1/recipes/{recipe_id}/relations", {
        params: { path: { recipe_id: recipeId } },
        body: input,
      });

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to add relation");
      }

      return { recipeId };
    },
    onSuccess: ({ recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      qc.invalidateQueries({ queryKey: ["recipe", recipeId, "graph"] });
      toast.success("Relation added");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Add relation failed: ${msg}`);
      }
    },
  });
}

/**
 * Remove a relation from a recipe.
 */
export function useRemoveRelation() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      toRecipeId,
      relationType,
      expectedEtag,
    }: {
      recipeId: string;
      toRecipeId: string;
      relationType: "prerequisite" | "followup" | "see_also";
      expectedEtag: string;
    }) => {
      const result = await typedApi.DELETE(
        "/v1/recipes/{recipe_id}/relations/{to_recipe_id}/{relation_type}",
        {
          params: {
            path: {
              recipe_id: recipeId,
              to_recipe_id: toRecipeId,
              relation_type: relationType,
            },
            query: { expected_etag: expectedEtag },
          },
        },
      );

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to remove relation");
      }

      return { recipeId };
    },
    onSuccess: ({ recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      qc.invalidateQueries({ queryKey: ["recipe", recipeId, "graph"] });
      toast.success("Relation removed");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Remove relation failed: ${msg}`);
      }
    },
  });
}

/**
 * Request a presigned upload URL for a recipe attachment.
 */
export function useRequestAttachmentUpload() {
  return useMutation({
    mutationFn: async ({
      recipeId,
      input,
    }: {
      recipeId: string;
      input: RecipeUploadRequest;
    }) => {
      const result = await typedApi.POST("/v1/recipes/{recipe_id}/attachments/request_upload", {
        params: { path: { recipe_id: recipeId } },
        body: input,
      });

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to request upload");
      }

      return result.data;
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Upload request failed: ${msg}`);
      }
    },
  });
}

/**
 * Confirm an attachment upload after the S3 PUT completes.
 */
export function useConfirmAttachmentUpload() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      input,
    }: {
      recipeId: string;
      input: RecipeUploadConfirm;
    }) => {
      const result = await typedApi.POST("/v1/recipes/{recipe_id}/attachments/confirm", {
        params: { path: { recipe_id: recipeId } },
        body: input,
      });

      if (result.error) {
        throw new Error(getErrorMessage(result.error) || "Failed to confirm upload");
      }

      return { recipeId };
    },
    onSuccess: ({ recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      toast.success("Attachment uploaded");
    },
    onError: (e) => {
      toast.error(`Upload confirmation failed: ${(e as Error).message}`);
    },
  });
}

/**
 * Remove an attachment from a recipe.
 */
export function useRemoveAttachment() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recipeId,
      path,
      expectedEtag,
    }: {
      recipeId: string;
      path: string;
      expectedEtag: string;
    }) => {
      const result = await typedApi.DELETE("/v1/recipes/{recipe_id}/attachments/{path}", {
        params: {
          path: { recipe_id: recipeId, path },
          query: { expected_etag: expectedEtag },
        },
      });

      if (result.error) {
        if (result.response.status === 409) {
          handleEtagMismatch();
          throw new Error("Recipe changed elsewhere");
        }
        throw new Error(getErrorMessage(result.error) || "Failed to remove attachment");
      }

      return { recipeId };
    },
    onSuccess: ({ recipeId }) => {
      qc.invalidateQueries({ queryKey: ["recipe", recipeId] });
      toast.success("Attachment removed");
    },
    onError: (e) => {
      const msg = (e as Error).message;
      if (msg !== "Recipe changed elsewhere") {
        toast.error(`Remove attachment failed: ${msg}`);
      }
    },
  });
}
